import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { useStorage } from '../hooks/useStorage';
import { Video } from '../types/video';
import { DialogShell, DialogActions } from './ConfirmDialog';

interface WBEditDecisionModalProps {
  video: Video;
  onClose: () => void;
  /** Called when the blob copy is ready — proceed to AIRecording editor with the copy */
  onEditCopy: (copiedVideo: Video) => void;
}

/**
 * A Whiteboard video can't be edited in place (Whiteboard has no link to open one of its
 * videos), so editing works on the user's own copy; the Whiteboard original stays unchanged.
 */
const WBEditDecisionModal: React.FC<WBEditDecisionModalProps> = ({ video, onClose, onEditCopy }) => {
  const { t } = useTranslation();
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyingRef = useRef(false);
  const { checkQuota, addStorage } = useStorage();
  const sizeBytes = Number(video.fileSize || video.file_size || 0);

  const handleEditCopy = async () => {
    if (copyingRef.current) return;
    copyingRef.current = true;
    setCopying(true);
    setError(null);
    try {
      // The copy is the user's own file and counts toward their storage.
      if (sizeBytes > 0 && !(await checkQuota(sizeBytes))) {
        setError(t('wb_import.copy_no_space', 'Not enough storage for a copy of this video.'));
        return;
      }
      const result = await apiClient.copyFromWhiteboard(video.id);
      if (result.data?.video) {
        if (sizeBytes > 0) await addStorage(sizeBytes);
        // Keep the fields the library already had (e.g. thumbnail); the copy's file wins.
        onEditCopy({ ...video, ...result.data.video });
        onClose();
        return;
      }
      setError(t('wb_import.copy_fail'));
    } catch {
      // Network failure: never leave the dialog stuck on "Creating copy…".
      setError(t('wb_import.copy_fail'));
    } finally {
      copyingRef.current = false;
      setCopying(false);
    }
  };

  const title = t('wb_import.edit_decision_title');

  return (
    <DialogShell title={title} onCancel={onClose} busy={copying} width={440}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingRight: 24 }}>
        <span style={{
          background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>WB</span>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
      </div>

      <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary, #6b7280)' }}>
        {t('wb_import.edit_decision_body')}
      </p>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary, #6b7280)' }}>
        {t('wb_import.edit_copy_warning')}
      </p>

      {error && (
        <p role="alert" style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--theme-error, #dc2626)' }}>
          {error}
        </p>
      )}

      <DialogActions
        confirmLabel={copying ? t('wb_import.copy_creating') : t('wb_import.edit_copy')}
        cancelLabel={t('cancel', 'Cancel')}
        busy={copying}
        onConfirm={handleEditCopy}
        onCancel={onClose}
      />
    </DialogShell>
  );
};

export default WBEditDecisionModal;
